/**
 * @fileoverview Returns DUST singleton for real-time notifications/events.
 */

import { DustProvider } from "./DustProvider";
import type { DustConnectionDiagnostics } from "./DustProvider";
import type { SequencerDiagnostics } from "./MessageSequencer";
import type { DustSingletonCreatedListener, DustSingletonOptions, DustSseEngineLiveDiagnostics } from "./types";

const dustInstance: { current: DustProvider | null } = { current: null };

const singletonCreatedListeners = new Set<DustSingletonCreatedListener>();

const notifySingletonCreated = (provider: DustProvider): void => {
  // Snapshot so a listener that unsubscribes itself can't skip the next one.
  [...singletonCreatedListeners].forEach((listener) => listener(provider));
};

declare global {
  interface Window {
    __dustDebug?: {
      diagnostics: () => DustConnectionDiagnostics | null;
      sequencer: () => SequencerDiagnostics | null;
      sequences: () => Record<string, number[]> | null;
      cleanupStream: (streamId: string) => void;
      /** Live engine fields for the direct-mode connection. */
      live: () => DustSseEngineLiveDiagnostics | null;
    };
  }
}

function installDustDebugOnWindow(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.__dustDebug = {
    diagnostics: () => dustInstance.current?.getConnectionDiagnostics() ?? null,
    sequencer: () => dustInstance.current?.getSequencerDiagnostics() ?? null,
    sequences: () => dustInstance.current?.getReceivedSequenceLog() ?? null,
    cleanupStream: (streamId: string) => dustInstance.current?.cleanupStream(streamId),
    live: () => dustInstance.current?.getLiveDiagnostics() ?? null,
  };
}

/**
 * Returns the per-tab DUST singleton for real-time communication.
 *
 * The connection runs in direct mode: its own EventSource bound to the tab's
 * session-targeted MITE. Each tab gets a distinct MITE, so multiple tabs never
 * conflict on a single shared identifier.
 */
export const getDustSingleton = (options: DustSingletonOptions): DustProvider => {
  const { railsEnv } = options;

  if (dustInstance.current) {
    return dustInstance.current;
  }

  const isDevOrStaging = railsEnv === "development" || railsEnv === "staging";
  dustInstance.current = new DustProvider({ isDevOrStaging });

  if (isDevOrStaging && typeof window !== "undefined") {
    installDustDebugOnWindow();
  }

  notifySingletonCreated(dustInstance.current);

  return dustInstance.current;
};

/**
 * Resets the DUST singleton. Useful for testing or cleanup.
 */
export const resetDustSingleton = (): void => {
  if (dustInstance.current) {
    dustInstance.current.destroy();
    dustInstance.current = null;
  }
};

/**
 * Sets a custom DUST singleton instance. Useful for Storybook enhancers and testing.
 * Call resetDustSingleton() first to clear any existing instance.
 */
export const setDustSingleton = (instance: DustProvider): void => {
  dustInstance.current = instance;
  notifySingletonCreated(instance);
};

/**
 * Calls `listener` once the DUST singleton exists: immediately if it already
 * does, otherwise when it is next created. Intended for non-React code (e.g.
 * PusherRequester) that starts listening before PusherProviderV2 has fetched
 * the /dust/auth config and created the connection.
 *
 * @param listener Called with the singleton, at most once per creation.
 * @return Unsubscribe function; a no-op once the listener has run.
 */
export const whenDustSingletonExists = (listener: DustSingletonCreatedListener): (() => void) => {
  if (dustInstance.current) {
    listener(dustInstance.current);
    return () => {};
  }

  const onCreated: DustSingletonCreatedListener = (provider) => {
    singletonCreatedListeners.delete(onCreated);
    listener(provider);
  };
  singletonCreatedListeners.add(onCreated);

  return () => {
    singletonCreatedListeners.delete(onCreated);
  };
};

/**
 * Returns the existing DUST singleton without creating one.
 * Safe to call when no instance exists — returns null.
 * Intended for non-React code (e.g. PusherRequester) that needs to
 * subscribe to DUST events when the singleton has been initialized
 * by PusherProviderV2.
 */
export const getDustSingletonIfExists = (): DustProvider | null => {
  return dustInstance.current;
};

/**
 * Returns DUST connection diagnostics from the singleton if it exists.
 * Safe to call even when no DUST instance is initialized — returns null.
 * Intended for error logging to disambiguate DUST-related failures.
 */
export const getDustConnectionDiagnostics = (): DustConnectionDiagnostics | null => {
  return dustInstance.current?.getConnectionDiagnostics() ?? null;
};

/**
 * Returns MessageSequencer diagnostics from the singleton if it exists.
 * Includes per-stream sequence state, pending buffers, and dedup stats.
 */
export const getDustSequencerDiagnostics = (): SequencerDiagnostics | null => {
  return dustInstance.current?.getSequencerDiagnostics() ?? null;
};

/**
 * Asks the DUST connection to drop its current SSE connection and open a fresh
 * one (gapless when possible). Safe to call when the singleton doesn't exist
 * (no-op). Intended for callers that detect streaming silence on a connection
 * the engine still considers healthy.
 */
export const forceDustReconnect = (): void => {
  dustInstance.current?.forceReconnect();
};
